import { css } from '@compiled/react';
import React, { useContext, useEffect } from 'react';
import { useLoaderData } from 'react-router-dom';
import { ContentType, Heading, InformationCardState, LearningControlPanel } from '@atlassian/learning-components';
import { token } from '@atlaskit/tokens';

import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { HelmetContext } from 'src/components/Helmet';
import { sendScreenEvent } from 'src/utils/analytics';
import { getSlug } from 'src/utils/slug';
import { getAssetUrl } from 'src/services/http/user';
import { CourseListWrapper, LearningPathInformationCard } from 'src/pages/LearningPathPage/children';
import { ContentHeader } from 'src/components/ContentHeader';
import { LearningPathLoaderProps } from 'src/pages/LearningPathPage/loader';
import { useProgressState, useCourseProgress, useLessonProgress } from 'src/store/progress/progressStore';
import { useSaveRecentlyViewed } from 'src/hooks/useSaveRecentlyViewed';
import { useReferrer } from 'src/hooks/useReferrer';
import { useRedirectObject } from 'src/hooks/useRedirectObject';
import { useSaveContent } from 'src/hooks/useSaveContent';
import { FIELD_ID } from 'src/pages/LearningPathPage/constants';
import { VersioningModalWrapper } from 'src/components/VersioningModalWrapper';
import { Disclaimer } from 'src/components/Disclaimer';
import { useLearningPathNavigation } from 'src/pages/LearningPathPage/utils/learningPathNavigation';
import { useStartContent } from 'src/hooks/useStartContent';

export const LearningPathPage: React.FC = () => {
  const { learningPath, notifications } = useLoaderData() as LearningPathLoaderProps;
  const { user, isLoading, hasError } = useAtlassianUser();
  const helmet = useContext(HelmetContext);
  const currentUrl = globalThis?.location?.href;
  const [, { fetchProgressForCourses, fetchProgressForLessons }] = useProgressState();
  const [{ progressForCourses }] = useCourseProgress();
  const [{ progressForLessons }] = useLessonProgress();
  const { referrerPath, isInitialized } = useReferrer();
  const { isContentSaved, saveContent } = useSaveContent({
    contentId: learningPath?.sys.id,
    contentType: ContentType.LEARNING_PATH,
    learningPathId: learningPath?.sys.id,
    user,
  });
  const redirectSlug = getSlug(learningPath);

  useRedirectObject({ ':pathId': redirectSlug });

  helmet.setHelmet({
    title: `${learningPath.title} | Learning - Atlassian Community`,
    seoTags: learningPath.seo,
  });

  useEffect(() => {
    if (!isLoading && !hasError && learningPath && isInitialized) {
      sendScreenEvent(
        'learningPathScreen',
        {
          learningPathSlug: redirectSlug,
          learningReferrerPath: referrerPath,
        },
        user?.account_id,
      );
    }
  }, [user, isLoading, hasError, learningPath, redirectSlug, referrerPath, isInitialized]);

  useEffect(() => {
    if (user && learningPath) {
      fetchProgressForCourses(learningPath, user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningPath, user]);

  useEffect(() => {
    if (user && learningPath) {
      const allLessonIds = learningPath.coursesCollection.items.flatMap((course) =>
        course.lessonsCollection.items.map((lesson) => lesson.sys.id),
      );

      fetchProgressForLessons(allLessonIds, user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningPath, user]);

  useSaveRecentlyViewed(learningPath?.sys.id, ContentType.LEARNING_PATH);

  const getCourseState = (): InformationCardState => {
    if (isLoading) {
      return InformationCardState.LOADING;
    }
    if (!user) {
      return InformationCardState.DEFAULT;
    }

    return InformationCardState.DEFAULT;
  };

  const { navigateToCourse, navigateToLesson } = useLearningPathNavigation();

  const startLearningPath = useStartContent({
    items: learningPath?.coursesCollection?.items,
    progress: progressForCourses,
    contentSlug: redirectSlug,
    contentType: ContentType.LEARNING_PATH,
    userId: user?.account_id,
    navigateToItem: navigateToCourse,
    fieldId: FIELD_ID,
    navigateToLesson,
    lessonsProgress: progressForLessons,
  });

  return (
    <>
      <VersioningModalWrapper notifications={notifications} resourceTitle={learningPath.title} />
      <main css={learningPathMainContainerStyles} data-testid="learning-path-page">
        <div
          css={[learningPathBannerStyles]}
          style={
            {
              '--desktop-banner': `url(${getAssetUrl('/images/contentBanners/learningPathBanner.svg')})`,
              '--mobile-banner': `url(${getAssetUrl('/images/contentBanners/mobileLearningPathBanner.svg')})`,
            } as Record<string, string>
          }
          role="img"
          aria-label="Learning Path Banner"
        />
        <div css={learningPathMainContentStyles}>
          <div css={learningPathFirstColumnStyles}>
            <div css={learningPathContentContainerStyles} data-testid="main-content-container">
              <div css={contentHeaderStyles}>
                <ContentHeader content={learningPath} contentType={ContentType.LEARNING_PATH} />
              </div>
            </div>
            <div css={learningPathInfoCardStyles}>
              <LearningPathInformationCard urlToShare={currentUrl} />
            </div>
            <div css={learningPathCenteredContentStyles}>
              <div css={learningPathCourseListWrapperStyles}>
                <div css={learningPathCoursesContentStyles} data-testid="courses-content-container">
                  <Heading level={4} color={token('color.text')}>
                    Courses list
                  </Heading>
                  <div css={learningPathWrapperStyles}>
                    <CourseListWrapper />
                  </div>
                </div>
              </div>
            </div>
            <Disclaimer
              disclaimer={learningPath?.disclaimer}
              entryId={learningPath?.sys.id}
              contentType={ContentType.LEARNING_PATH}
            />
          </div>
          <div css={learningPathAbsoluteInformationCardStyles}>
            <LearningPathInformationCard urlToShare={currentUrl} />
          </div>
        </div>
        <div css={learningPathStickyControlPanelStyles}>
          <LearningControlPanel
            isSaved={isContentSaved}
            urlToShare={currentUrl}
            onSave={user && saveContent}
            state={getCourseState()}
            contentType={ContentType.LEARNING_PATH}
            onClick={startLearningPath}
          />
        </div>
      </main>
    </>
  );
};

const learningPathMainContainerStyles = css({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'clip',
});

const learningPathMainContentStyles = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  gap: token('space.300'),
  maxWidth: '1200px',
  margin: '0 auto',
  justifyContent: 'flex-start',
  width: '100%',
  position: 'relative',
  zIndex: 1,
  padding: `0 ${token('space.300')}`,

  '@media (max-width: 965px)': {
    flexDirection: 'column',
    padding: `0 ${token('space.200')}`,
    gap: token('space.200'),
  },
});

const learningPathBannerStyles = css({
  minHeight: '119px',
  width: '100%',
  objectFit: 'cover',
  backgroundImage: 'var(--desktop-banner)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  marginTop: '136px',

  '@media (max-width: 1279px)': {
    marginTop: '0',
  },

  '@media (max-width: 768px)': {
    marginTop: '0',
  },

  '@media (max-width: 965px)': {
    backgroundImage: 'var(--mobile-banner)',
    minHeight: '60px',
  },
});

const contentHeaderStyles = css({
  display: 'flex',
  flex: 1,

  '@media (max-width: 965px)': {
    width: '100%',
    order: 1,
  },
});

const learningPathInfoCardStyles = css({
  display: 'none',

  '@media (max-width: 965px)': {
    display: 'block',
    width: '100%',
    order: 2,
    paddingBottom: token('space.300'),
  },
});

const learningPathCourseListWrapperStyles = css({
  position: 'relative',
  width: '100%',
});

const learningPathContentContainerStyles = css({
  display: 'flex',
  gap: token('space.300'),
  maxWidth: '1200px',
  margin: '0 auto',
  paddingTop: '45px',
  width: '100%',
  position: 'relative',
  zIndex: 1,

  '@media (max-width: 1200px)': {
    flexDirection: 'column',
    gap: token('space.300'),
  },
});

const learningPathFirstColumnStyles = css({
  display: 'flex',
  flex: '1 1 0',
  minWidth: 0,
  flexDirection: 'column',
  maxWidth: '100%',
  marginRight: '30px',

  '@media (max-width: 965px)': {
    width: '100%',
    maxWidth: '100%',
    order: 1,
  },
});

const learningPathAbsoluteInformationCardStyles = css({
  position: 'sticky',
  top: '65px',
  flex: '1 1 0',
  minWidth: '250px',
  maxWidth: '395px',
  width: 'auto',
  borderRadius: '8px',
  zIndex: 2,
  alignSelf: 'flex-start',
  paddingTop: '45px',
  paddingBottom: token('space.300'),
  backgroundColor: 'transparent',

  '@media (max-width: 965px)': {
    display: 'none',
  },
});

const learningPathCenteredContentStyles = css({
  padding: `${token('space.300')} 0`,
  display: 'flex',
  gap: token('space.300'),
  width: '100%',
  position: 'relative',
  zIndex: 1,

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '0',
    left: '0',
    width: '200vw',
    height: '100%',
    transform: 'translateX(-50%)',
    background: 'linear-gradient(90deg, #F8F8F8 0%, #F8F8F8 49.58%, rgba(248, 248, 248, 0.25) 100%)',
    zIndex: -1,
    pointerEvents: 'none',
  },

  '@media (max-width: 1200px)': {
    order: 3,
  },

  '@media (max-width: 965px)': {
    order: 3,
  },
});

const learningPathCoursesContentStyles = css({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  gap: token('space.200'),
  position: 'relative',
  zIndex: 1,

  '@media (max-width: 965px)': {
    minHeight: 'unset',
  },
});

const learningPathWrapperStyles = css({
  width: '100%',
});

const learningPathStickyControlPanelStyles = css({
  display: 'none',

  '@media (max-width: 965px)': {
    display: 'block',
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: token('color.text.inverse'),
    zIndex: 1000,
  },
});
